import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import * as PanelPopover from 'components/profile-panel/PanelPopover';
import { ProfileItemTriggerKeys } from 'components/profile-panel/PanelPopoverButtons';
import { usePanelPopoverContext } from 'components/profile-panel/PanelPopoverContext';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twJoin } from 'tailwind-merge';
import { Account } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface WebsiteFormValues {
    website: string;
}

const ProfileWebsite = () => {
    const { website } = useCurrentAccount();

    const methods = Form.useGivebutterForm<WebsiteFormValues>({
        defaultValues: {
            website: website ?? '',
        },
    });

    return (
        <PanelPopover.Root
            onOpenChange={open => {
                if (!open) {
                    methods.reset();
                }
            }}
        >
            <PanelPopover.Trigger asChild profileItem={ProfileItemTriggerKeys.Website}>
                <PanelPopover.TextButton
                    icon={faGlobe}
                    className={twJoin(website === null && 'text-gray-400')}
                >
                    <p className="max-w-60 truncate">{website ?? 'Add website'}</p>
                </PanelPopover.TextButton>
            </PanelPopover.Trigger>
            <PanelPopover.Content>
                <PanelPopover.Header>
                    <PanelPopover.Title icon={faGlobe}>Website</PanelPopover.Title>
                </PanelPopover.Header>
                <Form.Provider {...methods}>
                    <FormContent />
                </Form.Provider>
            </PanelPopover.Content>
        </PanelPopover.Root>
    );
};

const FormContent = () => {
    const { accountId } = useCurrentAccount();
    const { close } = usePanelPopoverContext();
    const { success, error } = useToast();

    const {
        control,
        handleSubmit,
        path,
        setError,
        resetFormButton,
        formState: { isDirty },
        reset,
    } = Form.useFormContext<WebsiteFormValues>();

    const { mutate: submit, isPending: isLoading } = useMutation<
        Account,
        GivebutterAxiosError,
        WebsiteFormValues
    >({
        mutationFn: values => updateAccount(accountId, values),
        onSuccess: ({ website }) => {
            success('Successfully updated website!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            close();
            reset({ website: website ?? '' });
        },
        onError: e => {
            const data = e.response?.data;
            const errors = data?.errors;

            if (errors) {
                for (const field in errors) {
                    setError(field as keyof WebsiteFormValues, {
                        message: errors[field][0],
                    });
                }
            } else {
                error(data?.message ?? 'Unable to update website');
            }

            resetFormButton();
        },
    });

    return (
        <form onSubmit={handleSubmit(values => submit(values))} className="flex flex-col gap-2 p-4">
            <Form.Container className="gap-3 p-2">
                <Form.Label className="sr-only" name={path('website')}>
                    Website
                </Form.Label>
                <Form.UseFormTextInputRoot control={control} name="website">
                    <Form.TextInputForm placeholder="Enter website" />
                </Form.UseFormTextInputRoot>
                <Form.Error name={path('website')} />
            </Form.Container>
            <div className="flex justify-between">
                <PanelPopover.CancelButton />
                <Form.SubmitButton size="sm" isSubmitting={isLoading} disabled={!isDirty}>
                    Save
                </Form.SubmitButton>
            </div>
        </form>
    );
};

export { ProfileWebsite };
